import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { UPSERT_EMPLOYEES_MUTATION } from "../../../../../../apis/organization-apis/people-apis/peopleApis";
import { LoadingButton } from "@mui/lab";
import ConfirmSnackbar from "../../../../../../components/Snackbar/ConfirmSnackbar";
import moment from "moment";
import DatePicker from "../../../../../../components/DatePicker/DatePicker";

const AddIndividualStaffDialog = ({
  open,
  onClose,
  staffList,
  refetch,
  close,
}) => {
  const [upsertEmployees] = useMutation(UPSERT_EMPLOYEES_MUTATION);
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const TransitionComponent = (props) => {
    return <Slide {...props} direction="left" />;
  };

  const initialValues = {
    employeeNumber: "",
    staffEmail: "",
    firstName: "",
    middleName: "",
    lastName: "",
    effectiveDate: "",
    employeeRole: "",
    reportingManager: "",
    staffType: "employment",
  };

  const validationSchema = Yup.object().shape({
    employeeNumber: Yup.string().required("Required"),
    staffEmail: Yup.string().email("Valid email required").required("Required"),
    firstName: Yup.string().required("Required"),
    lastName: Yup.string().required("Required"),
    effectiveDate: Yup.string().required("Required"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await upsertEmployees({
        variables: {
          employees: [
            {
              employee_number: values.employeeNumber,
              employee_email: values.staffEmail,
              first_name: values.firstName,
              middle_name: values.middleName || null,
              last_name: values.lastName,
              name: `${values.firstName} ${values?.middleName || ""} ${
                values.lastName
              }`,
              manager_email: values.reportingManager || null,
              effective_date: moment(values.effectiveDate).format("YYYY-MM-DD"),
              job_title: values.employeeRole || null,
              employment_type: values.staffType,
              termination_date: null,
              employment_status: "Active",
            },
          ],
        },
      });

      refetch();
      resetForm();
      onClose();
      close();
      setSnackbarInfo({
        message: response.data.upsertEmployees.message,
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    } catch (error) {
      console.error("Error adding staff member:", error.graphQLErrors || error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle fontWeight="bold">
          Add a Staff Member
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="employeeNumber"
                      as={TextField}
                      fullWidth
                      label="Employee Number"
                      error={touched.employeeNumber && !!errors.employeeNumber}
                      helperText={
                        touched.employeeNumber && errors.employeeNumber
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="staffEmail"
                      as={TextField}
                      fullWidth
                      label="Staff Email"
                      error={touched.staffEmail && !!errors.staffEmail}
                      helperText={touched.staffEmail && errors.staffEmail}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="firstName"
                      as={TextField}
                      fullWidth
                      label="First Name"
                      error={touched.firstName && !!errors.firstName}
                      helperText={touched.firstName && errors.firstName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="middleName"
                      as={TextField}
                      fullWidth
                      label="Middle Name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="lastName"
                      as={TextField}
                      fullWidth
                      label="Last Name"
                      error={touched.lastName && !!errors.lastName}
                      helperText={touched.lastName && errors.lastName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field name="effectiveDate">
                      {({ field, form }) => (
                        <DatePicker
                          fullWidth
                          value={field.value}
                          onChange={(date) => form.setFieldValue("effectiveDate", date)}
                          error={form.touched.effectiveDate && !!form.errors.effectiveDate}
                          helperText={form.touched.effectiveDate && form.errors.effectiveDate}
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="employeeRole"
                      as={TextField}
                      fullWidth
                      label="Employee Role"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="reportingManager"
                      as={TextField}
                      fullWidth
                      label="Reporting Manager"
                      select
                    >
                      {staffList?.getEmployees?.data?.length ? (
                        staffList.getEmployees.data.map((value, i) => (
                          <MenuItem value={value?.emails[0]?.email} key={i}>
                            {value?.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          No Manager to assign
                        </MenuItem>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <FormControl component="fieldset">
                      <FormLabel
                        sx={{ color: "black !important", fontWeight: "bold" }}
                      >
                        Staff Type
                      </FormLabel>
                      <Field name="staffType" as={RadioGroup} row>
                        <FormControlLabel
                          value="part-time"
                          control={<Radio />}
                          label="Part Time"
                        />
                        <FormControlLabel
                          value="full-time"
                          control={<Radio />}
                          label="Full Time"
                        />
                        <FormControlLabel
                          value="contract"
                          control={<Radio />}
                          label="Contract"
                        />
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>

                <DialogActions>
                  <Button onClick={onClose} variant="outlined">
                    Cancel
                  </Button>
                  <LoadingButton
                    type="submit"
                    color="primary"
                    variant="contained"
                    loading={isSubmitting}
                  >
                    Add Staff Member
                  </LoadingButton>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default AddIndividualStaffDialog;
